import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bffApiClient } from '@api/bff/client';
import { useApiToast } from '@hooks/useApiToast';
import type {
  CreateFeedbackRequest,
  ResolveFeedbackRequest,
  ResolveGapRequest,
  UpdateFeedbackRequest
} from '../types';

const SCRIPTS_KEY = 'scripts';

const invalidateScript = (
  queryClient: ReturnType<typeof useQueryClient>,
  masterId: string
) => {
  queryClient.invalidateQueries({ queryKey: [SCRIPTS_KEY, masterId] });
  queryClient.invalidateQueries({ queryKey: [SCRIPTS_KEY, 'children', masterId] });
};

export const useResolveGap = (masterId: string) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<
    void,
    Error,
    { gapId: string; body: ResolveGapRequest }
  >({
    mutationFn: async ({ gapId, body }) => {
      await bffApiClient.post(
        `/api/mqtt-scripts/${masterId}/gaps/${gapId}/resolve`,
        body
      );
    },
    onSuccess: () => {
      invalidateScript(queryClient, masterId);
      showSuccess('scripts.gapResolved');
    },
    onError: error => showError('scripts.gapResolveError', error)
  });
};

export const useMarkGapApplied = (masterId: string) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<
    void,
    Error,
    { gapId: string; appliedBy?: string | null }
  >({
    mutationFn: async ({ gapId, appliedBy }) => {
      await bffApiClient.post(
        `/api/mqtt-scripts/${masterId}/gaps/${gapId}/mark-applied`,
        { appliedBy: appliedBy ?? null }
      );
    },
    onSuccess: () => {
      invalidateScript(queryClient, masterId);
      showSuccess('scripts.gapMarkedApplied');
    },
    onError: error => showError('scripts.gapMarkAppliedError', error)
  });
};

export const useCreateFeedback = (masterId: string) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<{ id: string }, Error, CreateFeedbackRequest>({
    mutationFn: async body => {
      const response = await bffApiClient.post<{ id: string }>(
        `/api/mqtt-scripts/${masterId}/feedback`,
        body
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateScript(queryClient, masterId);
      showSuccess('scripts.feedbackAdded');
    },
    onError: error => showError('scripts.feedbackAddError', error)
  });
};

export const useUpdateFeedback = (masterId: string) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<
    void,
    Error,
    { feedbackId: string; body: UpdateFeedbackRequest }
  >({
    mutationFn: async ({ feedbackId, body }) => {
      await bffApiClient.put(
        `/api/mqtt-scripts/${masterId}/feedback/${feedbackId}`,
        body
      );
    },
    onSuccess: () => {
      invalidateScript(queryClient, masterId);
      showSuccess('scripts.feedbackUpdated');
    },
    onError: error => showError('scripts.feedbackUpdateError', error)
  });
};

export const useResolveFeedback = (masterId: string) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<
    void,
    Error,
    { feedbackId: string; body: ResolveFeedbackRequest }
  >({
    mutationFn: async ({ feedbackId, body }) => {
      await bffApiClient.post(
        `/api/mqtt-scripts/${masterId}/feedback/${feedbackId}/resolve`,
        body
      );
    },
    onSuccess: () => {
      invalidateScript(queryClient, masterId);
      showSuccess('scripts.feedbackResolved');
    },
    onError: error => showError('scripts.feedbackResolveError', error)
  });
};

export const useDeleteFeedback = (masterId: string) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useApiToast();

  return useMutation<void, Error, string>({
    mutationFn: async feedbackId => {
      await bffApiClient.delete(
        `/api/mqtt-scripts/${masterId}/feedback/${feedbackId}`
      );
    },
    onSuccess: () => {
      invalidateScript(queryClient, masterId);
      showSuccess('scripts.feedbackDeleted');
    },
    onError: error => showError('scripts.feedbackDeleteError', error)
  });
};
